import { Injectable } from '@angular/core';

import { DateTimeFormatterFormatType, LocaleService } from './locale.service';

@Injectable()
export class PtLocaleService extends LocaleService {
    private readonly locale = 'pt';

    constructor() {
        super();
    }

    format(date: Date, format: DateTimeFormatterFormatType, extended: boolean = false): string {
        switch (format) {
            case DateTimeFormatterFormatType.Date:
                return date.toLocaleDateString(this.locale);
            case DateTimeFormatterFormatType.Time:
                return extended 
                    ? date.toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) 
                    : date.toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit' });
        }
    }

    getCurrentLocaleToken(): string {
        return this.locale;
    }

    getCurrentWeekDaySeparator(): string {
        return 'a';
    }

    getDateExtended(date: Date, showWeekDay: boolean = false): string {
        const day = date.toLocaleDateString(this.locale, { day: '2-digit' });
        const month = date.toLocaleDateString(this.locale, { month: 'long' }).replace(/\b[a-z]/g, (x) => x.toUpperCase());
        const year = date.toLocaleDateString(this.locale, { year: 'numeric' });

        let weekday = date.toLocaleDateString(this.locale, { weekday : 'long'});
        weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

        return showWeekDay ? `${weekday}, ${day} de ${month} ${year}` : `${day} de ${month} ${year}`; 
    }

    getDateTimeExtended(date: Date, showWeekDay: boolean = false): string {
        const day = date.toLocaleDateString(this.locale, { day: '2-digit' });
        const month = date.toLocaleDateString(this.locale, { month: 'long' }).replace(/\b[a-z]/g, (x) => x.toUpperCase());

        let weekday = date.toLocaleDateString(this.locale, { weekday : 'long'});
        weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

        const time = date.toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit' });

        return showWeekDay ? `${weekday}, ${day} de ${month}, ${time}` : `${day} de ${month}, ${time}`; 
    }

    getDateShort(date: Date): string {
        const day = date.toLocaleDateString(this.locale, { day: '2-digit' });
        const month = date.toLocaleDateString(this.locale, { month: 'long' }).replace(/\b[a-z]/g, (x) => x.toUpperCase());
        return `${day} ${month.slice(0, 3)}`;
    }
    
    getDateMonthNameFormat(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat('pt-PT', { month: 'short' });
    }

    getElapsedTimeSlugs(): string[] {
        return ['agora', 'min', 'h', 'dias'];
    }
    
    getFiscalNumberRegex(): string {
        // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html
        return '(PT)?[0-9]{9}';
    }

    getPhoneNumberRegex(): string {
        return '^[9]{1}[0-9]{8}';
    }

    getPhraseConnectorsRegex(): string {
        return ['^(\\s*)(d?(a|e|i|o|u)s?)(\\s+)', '(\\s+)(d?(a|e|i|o|u)s?)(\\s+)', '(\\s+)(d?(a|e|i|o|u)s?)(\\s*)$'].join('|');
    }

    getRangeDateSeparator(): string {
        return 'a';
    }

    validateFiscalNumber(fiscalNumber: string): boolean {
        // https://gist.github.com/eresende/88562d2c4dc85b62cb0c
        let hasErrors = 0;

        if (
            fiscalNumber.substr(0, 1) !== '1' && // singular person
            fiscalNumber.substr(0, 1) !== '2' && // singular person
            fiscalNumber.substr(0, 1) !== '3' && // singular person
            fiscalNumber.substr(0, 2) !== '45' && // singular person non resident
            fiscalNumber.substr(0, 1) !== '5' && // colectiv person
            fiscalNumber.substr(0, 1) !== '6' && // public office
            fiscalNumber.substr(0, 2) !== '70' && // inheritage
            fiscalNumber.substr(0, 2) !== '71' && // colectiv person non resident
            fiscalNumber.substr(0, 2) !== '72' && // trust funds
            fiscalNumber.substr(0, 2) !== '77' && // public office
            fiscalNumber.substr(0, 2) !== '79' && // exceptional regime
            fiscalNumber.substr(0, 1) !== '8' && // individual admin
            fiscalNumber.substr(0, 2) !== '90' && // condominiums
            fiscalNumber.substr(0, 2) !== '91' && // condominiums
            fiscalNumber.substr(0, 2) !== '98' && // non residents
            fiscalNumber.substr(0, 2) !== '99' // civil partnerships
        ) {
            hasErrors = 1;
        }

        const check1 = parseInt(fiscalNumber.substr(0, 1), 10) * 9;
        const check2 = parseInt(fiscalNumber.substr(1, 1), 10) * 8;
        const check3 = parseInt(fiscalNumber.substr(2, 1), 10) * 7;
        const check4 = parseInt(fiscalNumber.substr(3, 1), 10) * 6;
        const check5 = parseInt(fiscalNumber.substr(4, 1), 10) * 5;
        const check6 = parseInt(fiscalNumber.substr(5, 1), 10) * 4;
        const check7 = parseInt(fiscalNumber.substr(6, 1), 10) * 3;
        const check8 = parseInt(fiscalNumber.substr(7, 1), 10) * 2;
        
        const total = check1 + check2 + check3 + check4 + check5 + check6 + check7 + check8;
        
        const div = (total / 11).toString();
        
        const modulo11 = total - parseInt(div, 10) * 11;

        let comp = 0;

        if (modulo11 === 1 || modulo11 === 0){ 
            comp = 0; 
        }
        else { 
            comp = 11 - modulo11;
        }
        
        const lastDigit = parseInt(fiscalNumber.substr(8, 1), 10) * 1;

        if ( lastDigit !== comp ){ 
            hasErrors = 1;
        }
        
        return hasErrors === 0;
    }
}
